import type { CompanyProfile, Receipt } from "@/api/types";
import { formatAmount } from "@/lib/money";
import { PrintAmountInWords, PrintFooter, PrintHeader, PrintSignature } from "./DocumentSheet";
import { usePrintBranding } from "./branding";

/**
 * A receipt.
 *
 * What it applies to is printed line by line rather than as one total: a customer holding this
 * needs to know which of their invoices it settled, and "NPR 40,000 received" against four open
 * invoices tells them nothing. Anything unapplied is named as an advance, because money on account
 * is a thing they are owed the knowledge of.
 */
export function ReceiptPrint({
  receipt,
  seller,
}: {
  receipt: Receipt;
  seller: CompanyProfile | undefined;
}) {
  const branding = usePrintBranding("receipt");
  return (
    <article>
      <PrintHeader
        seller={seller}
        scope="receipt"
        documentTitle="Receipt"
        number={receipt.number}
        date={receipt.date}
        buyerLabel="Received from"
        buyerName={receipt.customerName}
      />

      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">What this receipt settles</caption>
        <thead>
          <tr className="border-border border-b">
            <th scope="col" className="text-muted-foreground py-1.5 text-left text-xs font-medium">
              Applied to
            </th>
            <th scope="col" className="text-muted-foreground py-1.5 text-left text-xs font-medium">
              Method
            </th>
            <th scope="col" className="text-muted-foreground py-1.5 text-right text-xs font-medium">
              Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {receipt.applications.map((application) => (
            <tr key={application.invoiceId} className="border-muted border-b">
              <td className="num text-foreground py-1.5">{application.invoiceNumber}</td>
              <td className="text-muted-foreground py-1.5">{receipt.method}</td>
              <td className="num text-foreground py-1.5 text-right">
                {formatAmount(application.applied)}
              </td>
            </tr>
          ))}

          {Number(receipt.advance) !== 0 ? (
            <tr className="border-muted border-b">
              <td className="text-muted-foreground py-1.5">Unapplied — held as customer advance</td>
              <td className="text-muted-foreground py-1.5">{receipt.method}</td>
              <td className="num text-foreground py-1.5 text-right">
                {formatAmount(receipt.advance)}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <p className="text-foreground mt-4 text-right text-sm font-medium">
        Total received · NPR <span className="num">{formatAmount(receipt.amount)}</span>
      </p>

      <PrintAmountInWords scope="receipt" amount={receipt.amount} />

      {branding.showPaymentDetails && receipt.bankAccountName ? (
        <p className="text-muted-foreground mt-2 text-xs">Into {receipt.bankAccountName}</p>
      ) : null}

      <PrintSignature scope="receipt" byLabel="Received by" />

      <PrintFooter note={receipt.note} />
    </article>
  );
}
